import { useEffect, useRef, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Folder, FolderInput, Loader2 } from 'lucide-react';
import api from '@/lib/api';
import { toast } from 'sonner';

export function folderError(error, fallback) {
  if (!error?.response || error.isNetworkError || error.response.status === 0 || error.response.status >= 500) return fallback;
  const detail = error?.response?.data?.detail;
  return typeof detail === 'string' ? detail : fallback;
}

// The same destination picker serves folder and document moves. A destination
// must be selected explicitly; a failed load can never silently mean Unfiled.
export default function FolderMoveDialog({ open, onOpenChange, folder, documents = [], onSuccess }) {
  const [folders, setFolders] = useState([]);
  const [destination, setDestination] = useState(undefined);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [saveError, setSaveError] = useState('');
  const [moving, setMoving] = useState(false);
  const [completed, setCompleted] = useState(false);
  const pending = useRef(false);
  const generation = useRef(0);
  const ids = [...new Set((Array.isArray(documents) ? documents : [documents]).filter(Boolean).map(d => typeof d === 'string' ? d : d.id))];

  const load = async () => {
    const request = ++generation.current;
    setLoading(true);
    setLoadError('');
    try {
      const { data } = await api.get('/kb/folders/flat');
      if (request === generation.current) setFolders(data || []);
    } catch {
      if (request === generation.current) setLoadError('Folders could not be loaded. Try again before choosing a destination.');
    } finally {
      if (request === generation.current) setLoading(false);
    }
  };

  useEffect(() => {
    if (open) {
      setDestination(undefined);
      setSaveError('');
      setCompleted(false);
      load();
    }
    return () => { generation.current += 1; };
  }, [open]);

  const excluded = new Set();
  if (folder) {
    excluded.add(folder.id);
    // Following parent IDs also handles names containing SQL pattern symbols.
    let changed = true;
    while (changed) {
      changed = false;
      folders.forEach(f => {
        if (excluded.has(f.parent_id) && !excluded.has(f.id)) {
          excluded.add(f.id);
          changed = true;
        }
      });
    }
  }
  const candidates = folders.filter(f => !excluded.has(f.id));
  const rootName = folder ? 'Knowledge Base (top level)' : 'Unfiled';
  const currentParent = folder?.parent_id || null;
  const validDestination = destination !== undefined && (destination === null || candidates.some(f => f.id === destination));
  const canMove = !loading && !loadError && validDestination && !completed && (folder ? destination !== currentParent : ids.length > 0);

  const move = async () => {
    if (pending.current || !canMove) return;
    pending.current = true;
    setMoving(true);
    setSaveError('');
    try {
      const { data } = await api.post(folder ? `/kb/folders/${folder.id}/move` : '/kb/folders/move-documents', folder
        ? { new_parent_id: destination }
        : { document_ids: ids, folder_id: destination });
      if (!folder && data.failed_count > 0) {
        setCompleted(true);
        setSaveError(`${data.success_count} of ${ids.length} documents moved. ${data.failed_count} could not be moved. Close this dialog and review the document list before trying again.`);
        onSuccess?.();
      } else {
        toast.success(folder ? 'Folder moved' : `${data.success_count} document${data.success_count === 1 ? '' : 's'} moved`);
        onSuccess?.();
        onOpenChange(false);
      }
    } catch (error) {
      setSaveError(folderError(error, 'The move could not be completed. Check the destination and try again.'));
    } finally {
      pending.current = false;
      setMoving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={value => { if (!pending.current) onOpenChange(value); }}>
      <DialogContent className="max-w-md max-h-[90dvh] overflow-y-auto" closeClassName="h-11 w-11 top-2 right-2 flex items-center justify-center">
        <DialogHeader className="pr-8">
          <DialogTitle>{folder ? 'Move folder' : 'Move to Folder'}</DialogTitle>
          <DialogDescription className="break-words">
            {folder ? `Choose a new location for “${folder.name}” and its contents.` : `Choose a destination for ${ids.length} document${ids.length === 1 ? '' : 's'}.`}
          </DialogDescription>
        </DialogHeader>
        {loading ? <p role="status" className="flex items-center gap-2 text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" />Loading folders...</p> : loadError ? (
          <div className="space-y-3"><p role="alert" className="text-sm text-destructive-text">{loadError}</p><Button variant="outline" className="min-h-11" onClick={load}>Retry loading folders</Button></div>
        ) : (
          <fieldset disabled={moving || completed} className="min-w-0 space-y-2">
            <legend className="mb-2 text-sm font-medium">Destination</legend>
            <div className="max-h-[40dvh] overflow-y-auto space-y-2 p-1">
              {[{ id: null, name: rootName }, ...candidates].map(f => (
                <label key={f.id || 'root'} className={`flex min-h-11 items-start gap-3 rounded-lg border p-3 cursor-pointer focus-within:ring-2 focus-within:ring-ring ${destination === f.id ? 'border-primary bg-primary/10' : 'border-border hover:bg-secondary'}`}>
                  <input type="radio" name="folder-destination" value={f.id || ''} checked={destination === f.id} disabled={!!folder && f.id === currentParent} onChange={() => { setDestination(f.id); setSaveError(''); }} className="mt-1 accent-primary" />
                  {f.id ? <Folder className="w-4 h-4 mt-1 shrink-0 text-muted-foreground" /> : <FolderInput className="w-4 h-4 mt-1 shrink-0 text-muted-foreground" />}
                  <span className="min-w-0 break-words text-sm"><span className="font-medium">{f.name}</span>{!!folder && f.id === currentParent && <span className="text-muted-foreground"> (current location)</span>}{f.path && f.path !== `/${f.name}` && <span className="block text-xs text-muted-foreground">{f.path}</span>}</span>
                </label>
              ))}
            </div>
          </fieldset>
        )}
        {saveError && <p role="alert" className="text-sm text-destructive-text break-words">{saveError}</p>}
        <DialogFooter className="gap-2">
          <Button variant="outline" className="min-h-11" disabled={moving} onClick={() => onOpenChange(false)}>{completed ? 'Close' : 'Cancel'}</Button>
          {!completed && <Button className="min-h-11" onClick={move} disabled={moving || !canMove}>{moving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}{moving ? 'Moving...' : folder ? 'Move folder' : ids.length > 1 ? `Move ${ids.length} Documents` : 'Move Document'}</Button>}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
